<template>
  <v-dialog
    :model-value="modelValue"
    max-width="960"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card
      class="acceptance-preview"
      tabindex="0"
      @keydown.left="previous"
      @keydown.right="next"
    >
      <div class="d-flex align-center px-4 pt-3">
        <span class="text-subtitle-1 font-weight-medium mr-auto">
          {{ title }}
        </span>
        <span v-if="fileIds.length > 1" class="text-caption mr-2">
          {{
            $t("acceptance.preview.counter", {
              current: index + 1,
              total: fileIds.length,
            })
          }}
        </span>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          :title="$t('acceptance.preview.close')"
          :aria-label="$t('acceptance.preview.close')"
          @click="$emit('update:modelValue', false)"
        />
      </div>

      <div class="acceptance-preview-stage">
        <v-btn
          v-if="fileIds.length > 1"
          class="acceptance-preview-nav acceptance-preview-prev"
          icon="mdi-chevron-left"
          variant="flat"
          :disabled="index === 0"
          :title="$t('acceptance.preview.previous')"
          :aria-label="$t('acceptance.preview.previous')"
          @click="previous"
        />
        <img v-if="url" :src="url" :alt="title" />
        <span v-else-if="failed" class="text-body-2">
          {{ $t("acceptance.preview.load_failed") }}
        </span>
        <v-progress-circular v-else indeterminate color="grey" />
        <v-btn
          v-if="fileIds.length > 1"
          class="acceptance-preview-nav acceptance-preview-next"
          icon="mdi-chevron-right"
          variant="flat"
          :disabled="index === fileIds.length - 1"
          :title="$t('acceptance.preview.next')"
          :aria-label="$t('acceptance.preview.next')"
          @click="next"
        />
      </div>

      <div class="d-flex flex-wrap align-center px-4 pb-3 pt-2">
        <div class="mr-auto py-1">
          <div class="text-body-2">
            {{ (current && current.originalName) || "—" }}
          </div>
          <div v-if="current" class="text-caption text-grey-darken-1">
            {{ uploadedText }}
          </div>
        </div>
        <v-btn
          v-if="canRemove"
          color="red-darken-2"
          variant="text"
          prepend-icon="mdi-delete-outline"
          :disabled="disabled"
          @click="$emit('remove', currentId)"
        >
          {{ $t("acceptance.files.remove") }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";

export default {
  name: "AcceptanceFilePreviewDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    title: { type: String, default: "" },
    // images of one photo slot or checklist line, newest first
    fileIds: { type: Array, default: () => [] },
    startId: { type: String, default: null },
    // view.files: metadata (originalName, uploadedAt, uploadedBy) by id
    files: { type: Array, default: () => [] },
    // createBlobCache() from the page, which revokes the URLs
    blobs: { type: Object, required: true },
    removableIds: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "remove"],
  data: () => ({
    index: 0,
    url: null,
    failed: false,
  }),
  computed: {
    currentId() {
      return this.fileIds[this.index] || null;
    },
    current() {
      return this.files.find((file) => file.id === this.currentId) || null;
    },
    canRemove() {
      return Boolean(
        this.currentId && this.removableIds.includes(this.currentId),
      );
    },
    uploadedText() {
      const file = this.current || {};
      const date = file.uploadedAt
        ? moment(file.uploadedAt).format("DD/MM/YYYY HH:mm")
        : "—";
      const name = (file.uploadedBy && file.uploadedBy.name) || "—";
      return this.$t("acceptance.preview.uploaded_by", { name, date });
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.start();
    },
    startId() {
      if (this.modelValue) this.start();
    },
    // After a removal: stay on the same position, close when nothing is left.
    fileIds(ids) {
      if (!this.modelValue) return;
      if (!ids.length) {
        this.$emit("update:modelValue", false);
        return;
      }
      if (this.index > ids.length - 1) this.index = ids.length - 1;
    },
    currentId: {
      immediate: true,
      handler(fileId) {
        this.load(fileId);
      },
    },
  },
  methods: {
    start() {
      const at = this.fileIds.indexOf(this.startId);
      this.index = at >= 0 ? at : 0;
      this.load(this.currentId);
    },
    load(fileId) {
      this.url = null;
      this.failed = false;
      if (!fileId) return;
      this.blobs
        .get(fileId)
        .then((url) => {
          if (this.currentId === fileId) this.url = url;
        })
        .catch(() => {
          if (this.currentId === fileId) this.failed = true;
        });
    },
    previous() {
      if (this.index > 0) this.index -= 1;
    },
    next() {
      if (this.index < this.fileIds.length - 1) this.index += 1;
    },
  },
};
</script>

<style scoped>
.acceptance-preview-stage {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 240px;
  max-height: 70vh;
  margin: 8px 16px 0;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  overflow: hidden;
}
.acceptance-preview-stage img {
  display: block;
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}
.acceptance-preview-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.9);
}
.acceptance-preview-prev {
  left: 8px;
}
.acceptance-preview-next {
  right: 8px;
}
</style>
